// Unified theming engine (evolved from LinkVault's vaultTheme.js). The single
// seam between a persisted theme shape and the rendered surface. Value-agnostic
// and additive: the original LinkVault {bg_color,wallpaper,accent,preset}
// resolvers below are preserved verbatim so LinkVault stays byte-identical;
// generalized helpers for the richer {background,button,card,surface,
// corner_rounding} shape are appended in later tasks.

import { radius } from "../../theme/tokens";
import { DEFAULT_ACCENT } from "../GalleryTheme/galleryTheme";

// gordon-0918b-gallery-accent-border-all-four-spaces (2026-09-18): Gordon's
// repeat report — "the Gallery Themes Accent is not adding lines to the
// borders of all of the spaces... The whole border should be red" — found
// the Business Box, Profile box, Featured Wheel box and Wheel Grid space
// each hand-rolled their own border literal (some already wired to
// --gt-card-border, some hardcoded gray), free to drift out of sync. ONE
// helper for the {border, borderRadius} pair every themed gallery
// card/space shares, so they can't diverge again.
//
// Gallery-page consumers (rendered inside pages/Gallery/index.jsx's themed
// Grid, which always emits --gt-card-border/--gt-card-radius — see that
// file's galleryThemeVars) call this with no args and let the CSS custom
// properties cascade at paint time. Consumers with their own already-
// resolved JS-level accent/radius (LinkVault, which also renders standalone
// outside the Gallery page's themed Grid) pass `accent`/`radiusPx`
// explicitly, with their own neutral fallback color for parity with their
// pre-existing passthrough behaviour.
export const accentBorderSx = ({
  accent,
  fallback,
  radiusPx,
  radiusFallbackPx,
} = {}) => ({
  border: `1px solid ${accent || fallback || `var(--gt-card-border, ${DEFAULT_ACCENT})`}`,
  borderRadius:
    radiusPx != null
      ? `${radiusPx}px`
      : radiusFallbackPx != null
      ? `${radiusFallbackPx}px`
      : `var(--gt-card-radius, ${radius.md}px)`,
});

export const NEUTRAL_VAULT_BACKGROUND = `
  linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 50%),
  conic-gradient(from 90deg at 49.13% 50%, #F5EEDE -41.73deg, #CCFEFF 37.8deg, #F7FED5 140.4deg, #FDCFFE 216deg, #F5EEDE 318.27deg, #CCFEFF 397.8deg)
`;

export const NEUTRAL_VAULT_BACKGROUND_FULLPAGE = `
  linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 70%),
  conic-gradient(from 90deg at 49.13% 50%, #F5EEDE -41.73deg, #CCFEFF 37.8deg, #F7FED5 140.4deg, #FDCFFE 216deg, #F5EEDE 318.27deg, #CCFEFF 397.8deg)
`;

export const NEUTRAL_VAULT_ACCENT = null;

const VAULT_PRESETS = {};

export const resolvePreset = (presetId) =>
  (presetId && VAULT_PRESETS[presetId]) || null;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*[0-9.,%\s/]+\)$/i;
const HSL_COLOR = /^hsla?\(\s*[0-9.,%\s/deg]+\)$/i;
const OKLCH_COLOR = /^oklch\(\s*[0-9.,%\s/]+\)$/i;

export const isSafeCssColor = (v) => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s || s.length > 50) return false;
  return (
    HEX_COLOR.test(s) ||
    RGB_COLOR.test(s) ||
    HSL_COLOR.test(s) ||
    OKLCH_COLOR.test(s)
  );
};

// eslint-disable-next-line no-control-regex
const BG_BREAKOUT = /[;{}<>"']|url\s*\(|[\x00-\x1f]/i;
export const isSafeGradient = (v) =>
  typeof v === "string" &&
  v.length <= 2000 &&
  /(linear|radial|conic)-gradient\s*\(/i.test(v) &&
  !BG_BREAKOUT.test(v);

const isSafeCssBackground = (v) => isSafeCssColor(v) || isSafeGradient(v);

export const sanitizeHttpWallpaper = (raw) => {
  if (typeof raw !== "string" || raw.length > 2000) return null;
  if (/["'()<>;]|[\x00-\x1f]/.test(raw)) return null; // eslint-disable-line no-control-regex
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.href;
};

export const normalizeVaultTheme = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const preset = resolvePreset(raw.preset);
  const base = preset || {};
  return {
    ...base,
    ...(raw.bg_color ? { bg_color: raw.bg_color } : {}),
    ...(raw.wallpaper ? { wallpaper: raw.wallpaper } : {}),
    ...(raw.accent ? { accent: raw.accent } : {}),
    ...(raw.preset ? { preset: raw.preset } : {}),
  };
};

export const resolveVaultBackground = (theme /* , opts */) => {
  const t = normalizeVaultTheme(theme);
  if (t.wallpaper) {
    if (typeof t.wallpaper === "string" && /^https?:\/\//i.test(t.wallpaper)) {
      const safeUrl = sanitizeHttpWallpaper(t.wallpaper);
      if (safeUrl) return `url("${safeUrl}") center / cover no-repeat`;
    } else if (isSafeCssBackground(t.wallpaper)) {
      return t.wallpaper;
    }
  }
  if (t.bg_color && isSafeCssBackground(t.bg_color)) return t.bg_color;
  return null;
};

export const resolveVaultAccent = (theme) => {
  const t = normalizeVaultTheme(theme);
  return isSafeCssColor(t.accent) ? t.accent : NEUTRAL_VAULT_ACCENT;
};

// Re-exported so generalized resolvers (later tasks) and server-mirror tests
// can reuse the SAME allowlist. Backgrounds accept a safe color OR gradient.
export { isSafeCssBackground };

// ── Contrast: auto light/dark text per color pair (spec §2) ─────────────────
// Parse #RGB / #RRGGBB (ignores alpha) → [r,g,b] 0..255, or null if not hex.
export const hexToRgb = (hex) => {
  if (typeof hex !== "string") return null;
  let s = hex.trim().replace(/^#/, "");
  if (s.length === 3) s = s.split("").map((c) => c + c).join("");
  if (s.length !== 6 || /[^0-9a-f]/i.test(s)) return null;
  return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
};

const _srgbToLinear = (c) => {
  const x = c / 255;
  return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
};
const _relLuminance = ([r, g, b]) =>
  0.2126 * _srgbToLinear(r) + 0.7152 * _srgbToLinear(g) + 0.0722 * _srgbToLinear(b);
const _contrast = (l1, l2) =>
  (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

// Pure: pick '#111111' (near-black) or '#FFFFFF' text for a given FILL color,
// whichever has the higher WCAG contrast ratio. The fill color itself is never
// altered. `threshold` is advisory (spec open item, default 4.5:1) — the pick
// is by max-contrast; threshold is returned for callers that want to warn.
const NEAR_BLACK = "#111111";
export const pickTextColor = (fillHex, threshold = 4.5) => {
  const rgb = hexToRgb(fillHex);
  if (!rgb) return NEAR_BLACK;
  const L = _relLuminance(rgb);
  const blackL = _relLuminance(hexToRgb(NEAR_BLACK));
  const whiteL = _relLuminance([255, 255, 255]);
  const blackRatio = _contrast(L, blackL);
  const whiteRatio = _contrast(L, whiteL);
  void threshold; // advisory; kept in signature for callers that surface a warn
  return whiteRatio > blackRatio ? "#FFFFFF" : NEAR_BLACK;
};

// ── Generalized token theme (gallery / business_box) ────────────────────────
export const clampRounding = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(40, n));
};

// Coerce an arbitrary persisted token theme into a predictable object.
export const normalizeTheme = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  return {
    background: raw.background && typeof raw.background === "object" ? raw.background : null,
    button: raw.button && typeof raw.button === "object" ? raw.button : null,
    card: raw.card && typeof raw.card === "object" ? raw.card : null,
    surface: raw.surface && typeof raw.surface === "object" ? raw.surface : null,
    // gordon-0918-themes-modal-side-pane-icons-accent-borders-corners-
    // linkvault-bb: was `=== undefined` only. normalizeTheme's own OUTPUT
    // shape always sets corner_rounding to an explicit `null` when unset (see
    // this same function's return value), and resolveMatchGallery's
    // passthrough branch returns that already-normalized object straight
    // through -- callers that then re-normalize it (DataBox.jsx does, to get
    // a predictable shape after resolveMatchGallery) fed `null` back into
    // clampRounding, where `Number(null) === 0` is finite, silently turning
    // "unset" into a real `0` (square corners) on every re-normalize. `==
    // null` catches both null and undefined so normalizeTheme is idempotent.
    corner_rounding:
      raw.corner_rounding == null ? null : clampRounding(raw.corner_rounding),
    match_gallery: raw.match_gallery && typeof raw.match_gallery === "object" ? raw.match_gallery : null,
  };
};

// CSS `background` for a token theme's background token, or null (neutral).
export const resolveThemeBackground = (theme) => {
  const t = normalizeTheme(theme);
  const bg = t.background;
  if (!bg || !bg.value) return null;
  if (bg.type === "image") {
    if (typeof bg.value === "string" && /^https?:\/\//i.test(bg.value)) {
      const safe = sanitizeHttpWallpaper(bg.value);
      if (safe) return `url("${safe}") center / cover no-repeat`;
    }
    return isSafeCssBackground(bg.value) ? bg.value : null;
  }
  // 'color' or 'gradient'
  return isSafeCssBackground(bg.value) ? bg.value : null;
};

// Resolve one {fill,border} token pair into safe values + auto text color.
// Unsafe values fall back to null so the caller keeps its own default.
export const resolveColorPair = (pair) => {
  const p = pair && typeof pair === "object" ? pair : {};
  const fill = isSafeCssColor(p.fill) ? p.fill : null;
  const border = isSafeCssColor(p.border) ? p.border : null;
  return { fill, border, text: fill ? pickTextColor(fill) : null };
};

// Render-time resolution for Match-Gallery tokens (spec §3). For a surface
// (business_box/linkvault) rendered ON the Gallery page, any token whose
// match_gallery flag is true is read LIVE from the current gallery theme;
// everywhere else (or when unchecked) the surface's own stored value wins.
// "surface" match pulls gallery's SURFACE token only — never its background.
export const resolveMatchGallery = (surfaceTheme, galleryTheme, isOnGalleryPage) => {
  const s = normalizeTheme(surfaceTheme);
  if (!isOnGalleryPage || !s.match_gallery) {
    return s; // own values everywhere else, or when nothing is matched
  }
  const g = normalizeTheme(galleryTheme);
  const mg = s.match_gallery;
  return {
    ...s,
    surface: mg.surface ? g.surface : s.surface,
    button: mg.button ? g.button : s.button,
    card: mg.card ? g.card : s.card,
    corner_rounding: mg.corner_rounding ? g.corner_rounding : s.corner_rounding,
    // background is NEVER matched from gallery (spec §2).
  };
};
